import { createContext, useContext, useEffect, useMemo, useState } from "react";

const translations = {
  en: {
    brand: "Saha Lab",
    nav: { products: "Product", mission: "Why Saha", contact: "Contact" },
    cta: { lang: "العربية", langShort: "AR" },
    hero: {
      eyebrow: "Saha Lab — صحة لاب",
      tagline_1: "Healthy",
      tagline_2: "made",
      tagline_3: "obvious.",
      subheading:
        "We craft clean, nutritious food and beverages for everyone. Real ingredients. Honest taste. No B.S.",
      cta_primary: "See our first product",
      cta_secondary: "Our mission",
      coming_soon_badge: "Coming Soon",
      stat_1: "0g",
      stat_1_label: "Artificial sweeteners",
      stat_2: "100%",
      stat_2_label: "Clean ingredients",
      stat_3: "Soon",
      stat_3_label: "First product launching",
    },
    products: {
      eyebrow: "First Drop",
      title: "Vitamin Water, coming soon.",
      subtitle:
        "Our debut product is a functional vitamin water — clean hydration designed for everyday moments. Stay tuned for the official launch.",
      coming_soon: "Coming Soon",
      vw_title: "Vitamin Water",
      vw_desc:
        "A functional drink crafted with vitamins, real fruit, and zero artificial nonsense. The first chapter of Saha Lab.",
      vw_note: "Launch details revealed soon.",
    },
    mission: {
      eyebrow: "Why Saha Labs",
      title: "Three promises. One mission.",
      goals: [
        {
          title: "Clean ingredients.",
          desc: "Every formula starts with what we'd feed our families. Real food, real benefits, nothing to hide on the label.",
        },
        {
          title: "Healthy for everyone.",
          desc: "Nutritious food shouldn't be a luxury. We design our products so quality and access can coexist.",
        },
        {
          title: "The obvious choice.",
          desc: "When healthy tastes better and feels easier, the switch becomes effortless. That's our north star.",
        },
      ],
    },
    contact: {
      eyebrow: "Get in touch",
      title: "Say hello.",
      subtitle:
        "Wholesalers, partnerships, press, or just a friendly note. We'd love to hear from you.",
      email_label: "Email us",
      reply_note: "We read every message and reply within a few business days.",
    },
    footer: {
      tagline: "Healthy made obvious.",
      sections: { explore: "Explore", company: "Company", connect: "Connect" },
      links: {
        products: "Product",
        mission: "Mission",
        contact: "Contact",
        story: "Our Story",
        sustainability: "Sustainability",
        careers: "Careers",
        instagram: "Instagram",
        tiktok: "TikTok",
        linkedin: "LinkedIn",
      },
      copyright: "© 2026 Saha Lab. All rights reserved.",
    },
    cookies: {
      message:
        "We use a few essential cookies to make this site work. By continuing, you're cool with that.",
      accept: "Accept",
      decline: "Decline",
      learn_more: "Learn more",
    },
  },
  ar: {
    brand: "صحة لاب",
    nav: { products: "المنتج", mission: "لماذا صحة", contact: "تواصل معنا" },
    cta: { lang: "English", langShort: "EN" },
    hero: {
      eyebrow: "صحة لاب — Saha Lab",
      tagline_1: "الصحة",
      tagline_2: "خيارٌ",
      tagline_3: "بديهي.",
      subheading:
        "نصنع أطعمة ومشروبات نظيفة ومغذية للجميع. مكونات حقيقية. طعم صادق. بدون مساومات.",
      cta_primary: "اكتشف منتجنا الأول",
      cta_secondary: "مهمتنا",
      coming_soon_badge: "قريباً",
      stat_1: "٠جم",
      stat_1_label: "محليات صناعية",
      stat_2: "١٠٠٪",
      stat_2_label: "مكونات نظيفة",
      stat_3: "قريباً",
      stat_3_label: "أول منتج لنا",
    },
    products: {
      eyebrow: "الإطلاق الأول",
      title: "مياه الفيتامينات، قريباً.",
      subtitle:
        "منتجنا الأول هو مياه فيتامينات وظيفية — ترطيب نظيف لكل لحظات يومك. ترقّبوا الإطلاق الرسمي.",
      coming_soon: "قريباً",
      vw_title: "مياه الفيتامينات",
      vw_desc:
        "مشروب وظيفي مصنوع بالفيتامينات والفواكه الحقيقية ودون أي إضافات صناعية. الفصل الأول من صحة لاب.",
      vw_note: "تفاصيل الإطلاق ستكشف قريباً.",
    },
    mission: {
      eyebrow: "لماذا صحة لاب",
      title: "ثلاث وعود. مهمة واحدة.",
      goals: [
        {
          title: "مكونات نظيفة.",
          desc: "نبدأ كل تركيبة بما نقدمه لعائلاتنا. غذاء حقيقي، فوائد حقيقية، ولا شيء نخفيه في الملصق.",
        },
        {
          title: "صحة للجميع.",
          desc: "الغذاء الصحي ليس رفاهية. نصمم منتجاتنا لتجمع بين الجودة وسهولة الوصول.",
        },
        {
          title: "الخيار البديهي.",
          desc: "حين يصبح الصحي ألذ وأسهل، يصبح التحول طبيعياً. هذه بوصلتنا.",
        },
      ],
    },
    contact: {
      eyebrow: "تواصل معنا",
      title: "قل مرحباً.",
      subtitle:
        "موزعون، شراكات، صحافة، أو مجرد رسالة لطيفة. يسعدنا أن نسمع منك.",
      email_label: "راسلنا عبر البريد",
      reply_note: "نقرأ كل رسالة ونرد خلال أيام عمل قليلة.",
    },
    footer: {
      tagline: "الصحة خيارٌ بديهي.",
      sections: { explore: "استكشف", company: "الشركة", connect: "تواصل" },
      links: {
        products: "المنتج",
        mission: "مهمتنا",
        contact: "تواصل معنا",
        story: "قصتنا",
        sustainability: "الاستدامة",
        careers: "الوظائف",
        instagram: "إنستغرام",
        tiktok: "تيك توك",
        linkedin: "لينكد إن",
      },
      copyright: "© ٢٠٢٦ صحة لاب. جميع الحقوق محفوظة.",
    },
    cookies: {
      message:
        "نستخدم بعض ملفات تعريف الارتباط الأساسية لتشغيل هذا الموقع. بمواصلة التصفح، أنت توافق على ذلك.",
      accept: "موافق",
      decline: "رفض",
      learn_more: "اعرف المزيد",
    },
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState("en");

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      toggle: () => setLang((l) => (l === "en" ? "ar" : "en")),
      t: translations[lang],
      isRTL: lang === "ar",
    }),
    [lang]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
